import { forwardRef, useEffect, useState } from 'react';
import { cn } from '../../lib/utils';

// Clases base del <Input> de shadcn: las aplicamos por defecto para que NumberInput
// se vea igual donde antes se usaba <Input>. Como usamos cn() (tailwind-merge), un
// className custom (ej "w-16 px-2 rounded-xl") sobreescribe estas base sin conflicto.
const INPUT_BASE_CLASSES =
  'flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

export interface NumberInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
  /** Valor numérico controlado. */
  value: number;
  /** Se dispara con el número parseado (o `emptyValue`/`min` mientras el campo está vacío). */
  onChange: (value: number) => void;
  /** Valor al que cae el campo si queda vacío al perder el foco. Default 0. */
  min?: number;
  /** Valor reportado a onChange mientras el campo está vacío. Default = min. */
  emptyValue?: number;
}

/**
 * Input numérico que SÍ deja borrar el contenido (a diferencia del patrón
 * `value={n}` + `Number(e.target.value) || 1`, donde al vaciar volvía a 1).
 *
 * Truco: mientras el campo tiene foco, manda el borrador de texto (permite vacío,
 * "0", "1.", etc.) y NO se re-sincroniza desde la prop. Al perder el foco, si quedó
 * vacío/inválido, vuelve a `min`. onChange siempre recibe un número usable para los
 * cálculos (nunca NaN).
 *
 * Es un <input> nativo: reenvía className y el resto de props, así reemplaza
 * directamente a los <input type="number"> de los creadores sin tocar estilos.
 */
export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  ({ value, onChange, min = 0, emptyValue, onFocus, onBlur, className, ...props }, ref) => {
    const [draft, setDraft] = useState<string>(value != null ? String(value) : '');
    const [focused, setFocused] = useState(false);

    // Sincronizar desde afuera solo cuando NO se está editando (evita el snap-back).
    useEffect(() => {
      if (!focused) setDraft(value != null ? String(value) : '');
    }, [value, focused]);

    return (
      <input
        ref={ref}
        type="number"
        className={cn(INPUT_BASE_CLASSES, className)}
        value={draft}
        onFocus={(e) => {
          setFocused(true);
          onFocus?.(e);
        }}
        onChange={(e) => {
          const raw = e.target.value;
          setDraft(raw);
          if (raw === '' || raw === '-' || raw === '.') {
            onChange(emptyValue ?? min);
          } else {
            const n = Number(raw);
            if (!Number.isNaN(n)) onChange(n);
          }
        }}
        onBlur={(e) => {
          setFocused(false);
          const n = Number(draft);
          if (draft === '' || Number.isNaN(n)) {
            setDraft(String(min));
            onChange(min);
          } else {
            setDraft(String(n));
            onChange(n);
          }
          onBlur?.(e);
        }}
        {...props}
      />
    );
  }
);

NumberInput.displayName = 'NumberInput';
